import FavouriteButton from 'src/components/Buttons/FavouriteButton';
import { MarketDisplayData } from 'src/components/MarketSelector/MarketSelectorTable/types';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import MarketIcon from 'src/components/Utils/MarketIcon';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { MarketCategory } from 'src/constants/markets';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import { ZERO } from 'src/constants/math';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import NumLib from 'src/utils/NumLib';
import { cm } from 'src/utils/classNames';
import { UIMarket } from '@velocity-exchange/common';
import { BigNum, PERCENTAGE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { memo, useCallback } from 'react';
import useGetCellDisplayStatus from 'src/components/MarketSelector/MarketSelectorTable/hooks/useGetCellDisplayStatus';
import { getGridClasses } from 'src/components/MarketSelector/MarketSelectorTable/utils';
import MarketSelectorTableCell from 'src/components/MarketSelector/MarketSelectorTable/MarketSelectorTableCell';
import MarketSelectorTableMarketNameCell from 'src/components/MarketSelector/MarketSelectorTable/MarketSelectorTableMarketNameCell';
import useIsMarketFundingPaused from 'src/hooks/useIsMarketFundingPaused';
import { FundingRateUnit } from 'src/components/MarketStatsBar/FundingRateCard';

interface Props {
	currentMarketIsPerp: boolean;
	market: MarketDisplayData;
	fundingMultiplier: number;
	fundingRateUnit: FundingRateUnit;
	selected: boolean;
	toggleFavourite: (market: UIMarket) => void;
	onSelect: (market: UIMarket) => void;
	marketCategory: MarketCategory;
}

function MarketSelectorTableRow({
	currentMarketIsPerp,
	market,
	fundingMultiplier,
	fundingRateUnit,
	selected,
	toggleFavourite,
	onSelect,
	marketCategory,
}: Props) {
	const getDisplayStatus = useGetCellDisplayStatus(
		marketCategory === 'spot' ? false : currentMarketIsPerp,
		market.market.isPerp
	);
	const isMobileScreenSize = useIsMobileScreenSize();

	const isFundingPaused = useIsMarketFundingPaused(market.market.marketId);

	const fundingRateString = (() => {
		const funding = (market?.funding ?? 0) * fundingMultiplier;

		if (isNaN(funding) || !isFinite(funding)) {
			return null;
		}

		if (fundingRateUnit === 'h') {
			return NumLib.formatNum.toTradePrecisionString(funding);
		}

		return funding.toFixed(2);
	})();

	const lowPrice24h =
		!isNaN(market?.low ?? 0) && isFinite(market?.low ?? 0)
			? NumLib.formatNum.toTradePrecisionString(market?.low ?? 0)
			: null;
	const highPrice24h =
		!isNaN(market?.high ?? 0) && isFinite(market?.high ?? 0)
			? NumLib.formatNum.toTradePrecisionString(market.high ?? 0)
			: null;

	const handleFavouriteClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();
			e.preventDefault();
			toggleFavourite(market.market);
		},
		[toggleFavourite, market.market]
	);

	const handleSelect = useCallback(() => {
		onSelect(market.market);
	}, [onSelect, market.market]);

	return (
		<div
			className={cm(
				getGridClasses(currentMarketIsPerp),
				'max-w-full cursor-pointer px-md text-text-default hover:bg-button-disabled',
				selected && 'bg-button-disabled',
				'py-sm'
			)}
			onClick={handleSelect}
		>
			{/** Market */}
			{getDisplayStatus('market') && (
				<div className="flex flex-col">
					<div className="flex items-center gap-2">
						<FavouriteButton
							highlighted={market.isFavorite}
							onClick={handleFavouriteClick}
							starSizePx={16}
						/>

						<MarketIcon
							marketSymbol={market.market.market.symbol}
							sizeClass="size-2xl"
							customWidth={24}
							isSpot={market.market.isSpot}
						/>

						<MarketSelectorTableMarketNameCell
							marketName={market.market.uiSymbols.marketDisplaySymbol}
							marketIndex={market.market.market.marketIndex}
							isPerp={market.market.isPerp}
							isMobileScreenSize={isMobileScreenSize}
							currentMarketIsPerp={currentMarketIsPerp}
						/>
					</div>
				</div>
			)}

			{getDisplayStatus('price') && (
				<MarketSelectorTableCell>
					{market.price && market.price.gt(ZERO) ? (
						<NumberDisplay
							value={market.price}
							type="currency"
							decimals={getMarketPriceDecimals(market.baseAssetSymbol)}
						/>
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/** Basic 24h % Change that we only show on desktop. */}
			{!isMobileScreenSize && getDisplayStatus('priceChangePct') && (
				<MarketSelectorTableCell>
					{!isNaN(market.pricePercentChange ?? 0) ? (
						<NumberDisplay
							value={BigNum.fromPrint(
								(market.pricePercentChange ?? 0).toString(),
								PERCENTAGE_PRECISION_EXP
							)}
							type="percentage_signed"
							signDependentColor
						/>
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/* Stacked price and 24h % change on mobile, sorted by % change. */}
			{isMobileScreenSize && getDisplayStatus('priceChangePct') && (
				<div className="flex flex-col gap-y-1">
					<MarketSelectorTableCell>
						{market.price && market.price.gt(ZERO) ? (
							<NumberDisplay
								value={market.price}
								type="currency"
								decimals={getMarketPriceDecimals(market.baseAssetSymbol)}
							/>
						) : (
							<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
						)}
					</MarketSelectorTableCell>
					<MarketSelectorTableCell>
						{!isNaN(market.pricePercentChange ?? 0) ? (
							<NumberDisplay
								value={BigNum.fromPrint(
									(market.pricePercentChange ?? 0).toString(),
									PERCENTAGE_PRECISION_EXP
								)}
								type="percentage_signed"
								signDependentColor
							/>
						) : (
							<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
						)}
					</MarketSelectorTableCell>
				</div>
			)}

			{/** 24h Vol */}
			{getDisplayStatus('volume') && (
				<MarketSelectorTableCell>
					{!isNaN(market.volume ?? 0) ? (
						`$${NumLib.millify(market.volume ?? 0).displayString}`
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/** Open Interest */}
			{getDisplayStatus('openInterest') && (
				<MarketSelectorTableCell>
					{!isNaN(market.openInterest ?? 0) ? (
						`$${NumLib.millify(market.openInterest ?? 0).displayString}`
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/** Funding */}
			{getDisplayStatus('funding') && (
				<MarketSelectorTableCell>
					{isFundingPaused ? (
						<span className="text-star-yellow">Paused</span>
					) : fundingRateString ? (
						<>{fundingRateString}%</>
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/** 24h Low */}
			{getDisplayStatus('priceLow24h') && (
				<MarketSelectorTableCell>
					{lowPrice24h ? (
						`$${lowPrice24h}`
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}

			{/** 24h High */}
			{getDisplayStatus('priceHigh24h') && (
				<MarketSelectorTableCell>
					{highPrice24h ? (
						`$${highPrice24h}`
					) : (
						<SkeletonValuePlaceholder className="h-[18px] w-16" loading />
					)}
				</MarketSelectorTableCell>
			)}
		</div>
	);
}

export default memo(MarketSelectorTableRow);
